import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
const lightTheme = {
    name: 'light',
    colors: {
        // Primary brand colors
        sage: '#8FAB8F',
        blush: '#F4C2C2',
        teal: '#2F5F5F',
        cream: '#FAF8F5',
        // Text colors
        textPrimary: '#2F5F5F',
        textSecondary: '#6B8E6B',
        textMuted: '#8FAB8F',
        // Background colors
        bgPrimary: '#FAF8F5',
        bgSecondary: '#FFFFFF',
        bgTertiary: '#F8F6F3',
        // Accent colors
        accent: '#2F5F5F',
        accentHover: '#1F4F4F',
        // Border colors
        border: '#E5E5E5',
        borderLight: '#F0F0F0',
        // Status colors
        success: '#4A9A9A',
        warning: '#F4C2C2',
        error: '#D4A5A5',
    }
};
const darkTheme = {
    name: 'dark',
    colors: {
        // Primary brand colors
        sage: '#6B8E6B',
        blush: '#D4A5A5',
        teal: '#4A9A9A',
        cream: '#2A2A2A',
        // Text colors
        textPrimary: '#FAF8F5',
        textSecondary: '#D4A5A5',
        textMuted: '#8FAB8F',
        // Background colors
        bgPrimary: '#2A2A2A',
        bgSecondary: '#3A3A3A',
        bgTertiary: '#4A4A4A',
        // Accent colors
        accent: '#4A9A9A',
        accentHover: '#5AAAAA',
        // Border colors
        border: '#4A4A4A',
        borderLight: '#5A5A5A',
        // Status colors
        success: '#6B8E6B',
        warning: '#D4A5A5',
        error: '#C49595',
    }
};
const ThemeContext = createContext(undefined);
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(lightTheme);
    // Auto-detect system preference on mount and listen for changes
    useEffect(() => {
        const savedTheme = localStorage.getItem('wholeMommaTheme');
        if (savedTheme) {
            // Use saved preference if available
            setThemeState(savedTheme === 'dark' ? darkTheme : lightTheme);
        }
        else {
            // Auto-detect system preference
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setThemeState(prefersDark ? darkTheme : lightTheme);
        }
        // Listen for system theme changes
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            // Only auto-switch if no manual preference is saved
            if (!localStorage.getItem('wholeMommaTheme')) {
                setThemeState(e.matches ? darkTheme : lightTheme);
            }
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);
    // Apply theme to document root
    useEffect(() => {
        const root = document.documentElement;
        // Apply CSS custom properties
        Object.entries(theme.colors).forEach(([key, value]) => {
            root.style.setProperty(`--color-${key}`, value);
        });
        // Add theme class to body
        document.body.className = `theme-${theme.name}`;
        // Save to localStorage
        localStorage.setItem('wholeMommaTheme', theme.name);
    }, [theme]);
    const toggleTheme = () => {
        setThemeState(theme.name === 'light' ? darkTheme : lightTheme);
    };
    const setTheme = (themeName) => {
        setThemeState(themeName === 'light' ? lightTheme : darkTheme);
    };
    return (_jsx(ThemeContext.Provider, { value: { theme, toggleTheme, setTheme }, children: children }));
};
